// ============================================================
// DefencePrompt - the reaction prompt: "N hit(s) incoming", the table
// ruling, Dodge / Parry / No Defence with the numbers the roll will
// use, the standing reaction's countdown, and the queue of other
// reactions owed.
//
// Split from CombatLogPanel together with the state it needs: the
// per-defender ruling, the derived reaction availability, and the
// standing reaction's countdown. The panel calls the hooks (in its
// original order) and renders this when a prompt is up.
// ============================================================
import { useEffect, useState } from 'react';
import clsx from '../utils/clsx';
import { standingChoice, STANDING_COUNTDOWN_S } from '../engine/standingReaction';
import { attackerDefenceMods } from '../engine/defenceChain';
import { reactionsAgainst, resolveReactionAvailability } from '../engine/reactionAvailability';
import { withDefenderMods } from '../engine/defenceCalc';
import { resolveReactionOptions } from '../engine/settings/resolve';
import NumberInput from './NumberInput';
import { clock } from './CombatPrompts';
import common from '../styles/common.module.css';
import styles from '../styles/CombatLogPanel.module.css';

/**
 * THE TABLE RULING (v8.366.0): a modifier typed on the banner, for THIS
 * defender's reaction and its chain only - keyed by the prompt's tab, so a
 * second combatant waiting behind starts at zero; forgotten once no
 * prompt is on screen.
 */
export function useDefenceRulings(defence) {
  const [rulings, setRulings] = useState({});
  const manualMod = (defence && rulings[defence.tabId]) || 0;
  const setManualMod = (next) => defence && setRulings((r) => ({ ...r, [defence.tabId]: next }));
  useEffect(() => {
    if (!defence) setRulings({});
  }, [defence]);
  return { manualMod, setManualMod };
}

/**
 * The reactions this defender may take against this attack: after the attacker's denials, and a parry only against
 * a blade unless they can parry shots. The attacker's own gate asks the same (reactionsAgainst), so a prompt that
 * appears always has something to offer.
 */
function offeredReactions(availability, atkDenial, defence, defenceTarget) {
  const attack = { isMelee: !!defence?.isMelee, canParryRanged: !!defenceTarget?.parry?.canParryRanged };
  return reactionsAgainst(availability, atkDenial, attack);
}

/**
 * Everything the prompt shows, derived from the one place that decides
 * each part. `defenceTarget` is folded, so the number on a button is the
 * number that will be rolled; solo play has no frozen target and falls
 * back to the live projection. Frenzy, Battle Rage and the condition
 * gates come from `resolveReactionAvailability` - reimplementing half of
 * it here is what once offered a Dodge the defence chain would refuse -
 * and the attacker's denial is the chain's own derivation.
 */
export function deriveDefence({ defence, currentTab, permissions, standingReaction }) {
  const defenceTarget = withDefenderMods(defence?.frozenTarget || currentTab?.target || null) || undefined;
  const availability = resolveReactionAvailability(defenceTarget, resolveReactionOptions(permissions));
  const atkDenial = attackerDefenceMods(
    defence?.slot?.specialRules,
    defenceTarget?.parry?.parryWeaponSpecials,
    defence?.attackerReactionMods ?? defenceTarget?.attackerReactionMods ?? null,
  );
  const offered = offeredReactions(availability, atkDenial, defence, defenceTarget);
  const standing = defence
    ? standingChoice(standingReaction, {
        ...offered,
        isMelee: !!defence.isMelee,
        canParryRanged: !!defenceTarget?.parry?.canParryRanged,
      })
    : null;
  return { defenceTarget, availability, atkDenial, standing };
}

/** The prompt's identity: a new attack on the same defender is a new prompt. */
export function defenceKeyOf(defence) {
  return defence ? `${defence.tabId}:${defence.createdAt ?? defence.attackTs ?? ''}` : null;
}

/**
 * THE STANDING REACTION (v8.325.0). The defender's standing choice
 * answers the prompt's first question after a countdown; any click on
 * the prompt overrides it, and "Ask me instead" stops it for this one.
 * `armed` says whether this client counts down at all.
 */
export function useStandingAuto({ armed, defenceKey, standing, handleDefenceChoice, pendingDefence, manualMod }) {
  const [autoState, setAutoState] = useState({ key: null, left: 0, cancelled: false });
  useEffect(() => {
    if (!armed) return undefined;
    if (autoState.key !== defenceKey) {
      setAutoState({ key: defenceKey, left: STANDING_COUNTDOWN_S, cancelled: false });
      return undefined;
    }
    if (autoState.cancelled) return undefined;
    if (autoState.left <= 0) {
      handleDefenceChoice(standing, pendingDefence, manualMod);
      return undefined;
    }
    const t = setTimeout(() => setAutoState((s) => (s.key === defenceKey ? { ...s, left: s.left - 1 } : s)), 1000);
    return () => clearTimeout(t);
  }, [armed, defenceKey, autoState, standing, handleDefenceChoice, pendingDefence, manualMod]);
  return {
    live: armed && autoState.key === defenceKey && !autoState.cancelled,
    left: autoState.left,
    cancel: () => setAutoState((s) => ({ ...s, cancelled: true })),
  };
}

const AUTO_VERB = { dodge: 'Dodging', parry: 'Parrying' };

function AutoCountdown({ standing, left, onCancel }) {
  return (
    <span className={styles.defenceAuto} data-testid="defence-auto" data-choice={standing}>
      {AUTO_VERB[standing] ?? 'Taking it'} in {left}…
      <button type="button" className={common.btnSmall} data-testid="defence-auto-cancel" onClick={onCancel}>
        Ask me instead
      </button>
    </span>
  );
}

/** The table ruling: -10, the number, +10. */
function RulingControls({ manualMod, setManualMod, disabled }) {
  return (
    <span className={styles.defenceRuling} data-testid="defence-ruling">
      <button
        type="button"
        className={common.btnSmall}
        disabled={disabled}
        onClick={() => setManualMod(manualMod - 10)}
        aria-label="Ruling -10"
        data-testid="defence-mod-minus"
      >
        −10
      </button>
      <NumberInput
        className={common.input}
        style={{ width: 52 }}
        value={manualMod}
        step={5}
        allowNegative
        disabled={disabled}
        aria-label="Reaction modifier - a table ruling for this reaction only"
        title="A table ruling for this reaction and its chain only: bad footing, a blinding flash, the GM's call."
        onChange={setManualMod}
        data-testid="defence-mod"
      />
      <button
        type="button"
        className={common.btnSmall}
        disabled={disabled}
        onClick={() => setManualMod(manualMod + 10)}
        aria-label="Ruling +10"
        data-testid="defence-mod-plus"
      >
        +10
      </button>
    </span>
  );
}

/**
 * Dodge and Parry, each showing the number the roll ACTUALLY uses. It
 * used to show the raw Agility while `getAvailableDefenceSteps` rolled
 * against agility + stance + condition mod, so a crippled combatant's
 * button read as though nothing were wrong with them. Attacker-side
 * Feinting and Telegraphed shift it too (v8.287.0).
 */
function ReactionButtons({ p, choose }) {
  const { defence, derived, manualMod } = p;
  const { defenceTarget, availability, atkDenial } = derived;
  const { canDodge, canParry } = offeredReactions(availability, atkDenial, defence, defenceTarget);
  return (
    <>
      {canDodge && (
        <button className={common.btnDodge} disabled={p.defenceResolving} onClick={() => choose('dodge')}>
          Dodge (Ag {defenceTarget.dodge.agility + availability.dodgeMod + atkDenial.dodgeMod + manualMod})
        </button>
      )}
      {canParry && (
        <button className={common.btnParry} disabled={p.defenceResolving} onClick={() => choose('parry')}>
          Parry (WS {defenceTarget.parry.weaponSkill + availability.parryMod + atkDenial.parryMod + manualMod})
        </button>
      )}
    </>
  );
}

/** The answers: the ruling, the reactions on offer (or why none are), No Defence, and the GM's force clear. */
function DefenceAnswers({ p }) {
  const { defenceResolving, manualMod, pendingDefence, handleDefenceChoice } = p;
  const disabledBy = p.derived.availability.disabledBy;
  return (
    <>
      <RulingControls manualMod={manualMod} setManualMod={p.setManualMod} disabled={defenceResolving} />
      <ReactionButtons p={p} choose={(kind) => handleDefenceChoice(kind, pendingDefence, manualMod)} />
      {disabledBy.length > 0 && (
        <span className={styles.defenceBlocked} data-testid="defence-blocked-reason">
          {disabledBy.join(', ')} - no reaction
        </span>
      )}
      <button
        className={common.btnSmall}
        disabled={defenceResolving}
        onClick={() => handleDefenceChoice('none', pendingDefence)}
      >
        No Defence
      </button>
      {p.isGm && p.onGmForceClear && (
        <button
          className={common.btnSmall}
          style={{ marginLeft: 8, color: 'var(--danger)' }}
          onClick={p.onGmForceClear}
          title="Force clear this defence prompt (GM only)"
        >
          Force Clear
        </button>
      )}
    </>
  );
}

/**
 * THE REST OF THE QUEUE, on the prompt itself. One area attack owes five
 * reactions at once, and a count alone ("4 more waiting") tells you the
 * fight is stalled without telling you on whom. Each name is the button:
 * Respond picks that character up and points the prompt at them.
 */
function ReactionQueue({ queue, disabled, onRespond, onAcceptAll }) {
  return (
    <div className={styles.reactionQueue} data-testid="reaction-queue">
      {queue.map((r) => (
        <button
          key={r.combatantId}
          className={common.btnSmall}
          data-testid={`respond-to-${r.combatantId}`}
          data-volume={r.volume}
          disabled={disabled}
          onClick={() => onRespond?.(r.combatantId)}
          title={`Act as ${r.name} and answer their reaction`}
        >
          {r.name}
        </button>
      ))}
      {onAcceptAll && (
        <button
          className={common.btnSmall}
          data-testid="accept-all-reactions"
          disabled={disabled}
          onClick={onAcceptAll}
          title="Nobody else reacts - apply every remaining attack"
        >
          Accept all remaining
        </button>
      )}
    </div>
  );
}

/** "Al - 2 hit(s) incoming from Bob (Bolter):" - THE DEFENDER'S NAME FIRST; it is no longer implied by the tab. */
function incomingLabel(reactionName, defence) {
  return (
    (reactionName ? reactionName + ' - ' : '') +
    defence.hits.filter((h) => !h.ffBlocked).length +
    ' hit(s) incoming' +
    (defence.attackerName ? ' from ' + defence.attackerName : '') +
    (defence.slot?.weaponName ? ' (' + defence.slot.weaponName + ')' : '') +
    ':'
  );
}

/** The prompt's status line: the standing countdown, the reaction clock, how many wait behind. */
function PromptStatus({ p }) {
  const { auto, reactionRemainingMs, reactionsWaiting } = p;
  return (
    <>
      {auto.live && <AutoCountdown standing={p.derived.standing} left={auto.left} onCancel={auto.cancel} />}
      {reactionRemainingMs !== null && reactionRemainingMs !== undefined && (
        <span className={styles.defenceCountdown} data-testid="defence-countdown">
          {clock(reactionRemainingMs)}
        </span>
      )}
      {reactionsWaiting > 0 && (
        <span className={styles.defenceWaiting} data-testid="reactions-waiting">
          {reactionsWaiting} more waiting
        </span>
      )}
    </>
  );
}

/**
 * The initial defence prompt, before any chain. LOUD LIFTS IT OFF THE
 * PANEL: a reaction can be owed by somebody this client is not looking at
 * - and the log panel is not always on screen - so when it is mine it
 * becomes a fixed overlay no other panel can hide. Calm keeps the inline bar.
 */
export default function DefencePrompt(p) {
  const { defenceResolving, reactionVolume, reactionQueue } = p;
  return (
    <div
      className={clsx(
        styles.defenceBar,
        reactionVolume === 'loud' && styles.defenceAlarm,
        defenceResolving && styles.defenceResolving,
      )}
      role="alert"
      data-testid="defence-prompt"
      data-volume={reactionVolume}
    >
      <span className={styles.defenceLabel}>{incomingLabel(p.reactionName, p.defence)}</span>
      <PromptStatus p={p} />
      {defenceResolving ? (
        <span style={{ color: 'var(--warn)', fontStyle: 'italic' }}>Resolving...</span>
      ) : (
        <DefenceAnswers p={p} />
      )}
      {reactionQueue?.length > 0 && (
        <ReactionQueue
          queue={reactionQueue}
          disabled={defenceResolving}
          onRespond={p.onRespondToReaction}
          onAcceptAll={p.onAcceptAllReactions}
        />
      )}
    </div>
  );
}
